import { Moon, Sun } from 'lucide-react';
import { useThemeStore } from '@/stores/theme';
import { Button } from './ui/Button';

/**
 * One-tap light/dark switch, in the sidebar footer and the phone header.
 *
 * Settings has a Light / Dark choice and keeps it - that is where the option
 * is explained. This is the other need: the room got bright, flip it now.
 * Three taps into Settings is too far for that.
 *
 * It reads the STORE, not the DOM. The previous version read
 * `documentElement.classList` once at mount into local state, and because it
 * is mounted twice - sidebar and phone header - the two copies stopped
 * agreeing the moment either was pressed: one showed a sun while the page
 * was already light. The store is the single fact both render from.
 */
export function ThemeToggle({ className }: { className?: string }) {
  const showing = useThemeStore((s) => s.pref);
  const setPref = useThemeStore((s) => s.setPref);
  const next = showing === 'dark' ? 'light' : 'dark';

  // The accessible name carries BOTH halves: what is showing now and what a
  // press does. "Switch to dark mode" alone tells a screen-reader user the
  // verb but leaves them to infer the state; "Dark mode" alone is a noun with
  // no verb, and the icon that sighted users read the state from is hidden
  // from them entirely.
  const label = `Switch to ${next} mode (currently ${showing})`;

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      onClick={() => setPref(next)}
      aria-label={label}
      title={label}
      data-testid="theme-toggle"
      data-theme={showing}
    >
      {showing === 'dark' ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
    </Button>
  );
}
